import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageSkeleton } from "@/components/PageSkeleton";
import { formatIDR } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Wallet, Landmark, Smartphone, Building2, Plus, Pencil, Trash2, ReceiptText, Gift } from "lucide-react";

const ICONS = {
  "Kas Tunai": Wallet,
  "Bank BCA": Building2,
  "Bank Mandiri": Landmark,
  "Bank BRI": Landmark,
  "QRIS / E-Wallet": Smartphone,
};
const KIND_ICON = { Kas: Wallet, Bank: Landmark, "E-Wallet": Smartphone };
const KINDS = ["Kas", "Bank", "E-Wallet"];

export default function CashBank() {
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", kind: "Bank" });
  const [editFor, setEditFor] = useState(null);
  const [newBalance, setNewBalance] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/dashboard/summary?period=all").then((r) => setSummary(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => setAccounts([]));
  };
  useEffect(() => { load(); }, []);

  if (!summary) return <PageSkeleton stats={3} rows={0} />;

  const total = summary.cash + summary.bank_total + summary.ewallet;

  const addAccount = async () => {
    if (!addForm.name.trim()) { toast.error("Nama akun wajib diisi"); return; }
    setBusy(true);
    try {
      await api.post("/accounts", { name: addForm.name.trim(), kind: addForm.kind });
      toast.success("Akun ditambahkan");
      setAddOpen(false); setAddForm({ name: "", kind: "Bank" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveBalance = async () => {
    if (newBalance === "" || isNaN(parseFloat(newBalance))) { toast.error("Isi saldo baru yang valid"); return; }
    setBusy(true);
    try {
      await api.put("/accounts/balance", { name: editFor.name, balance: parseFloat(newBalance) });
      toast.success("Saldo diperbarui");
      setEditFor(null); setNewBalance(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeAccount = async (a) => {
    try {
      const res = await api.post("/deletion-requests", { resource_type: "account", resource_id: a.id, label: a.name });
      toast.success(res.data.pending ? "Permintaan hapus dikirim, menunggu persetujuan Owner" : "Akun dihapus");
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Kas Tunai & Saldo Bank</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Posisi dana dari seluruh transaksi. Saldo dapat dikoreksi manual.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-account-btn" size="sm" className="gap-1.5 h-8 text-xs shrink-0"><Plus className="h-3.5 w-3.5" /> Tambah Akun</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-base">Tambah Akun Kas / Bank</DialogTitle>
              <DialogDescription className="text-xs">Akun baru otomatis tampil di pilihan metode pembayaran transaksi.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <div className="space-y-1">
                <Label className="text-xs">Nama Akun</Label>
                <Input data-testid="acc-name" className="h-9 text-xs" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="cth. Bank BNI / Kas Kecil" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Jenis</Label>
                <Select value={addForm.kind} onValueChange={(v) => setAddForm({ ...addForm, kind: v })}>
                  <SelectTrigger data-testid="acc-kind" className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k} className="text-xs">{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAddOpen(false)}>Batal</Button>
              <Button onClick={addAccount} disabled={busy} data-testid="acc-submit" variant="gold" size="sm" className="h-8 text-xs">Simpan Akun</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 sm:p-5 text-white relative overflow-hidden border border-[#C9A227]/30" style={{ backgroundImage: "linear-gradient(135deg, #0A1633 0%, #12306b 60%, #1e3fae 100%)" }}>
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[#C9A227]/15 blur-3xl" />
        <p className="text-xs text-blue-200 relative">Total Saldo Keseluruhan</p>
        <p className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-1.5 relative" data-testid="total-balance">{formatIDR(total)}</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 relative">
          <div><p className="text-[11px] text-blue-200">Kas Tunai</p><p className="font-semibold text-base">{formatIDR(summary.cash)}</p></div>
          <div><p className="text-[11px] text-blue-200">Total Bank</p><p className="font-semibold text-base">{formatIDR(summary.bank_total)}</p></div>
          <div><p className="text-[11px] text-blue-200">QRIS / E-Wallet</p><p className="font-semibold text-base">{formatIDR(summary.ewallet)}</p></div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {accounts.map((a) => {
          const Icon = ICONS[a.name] || KIND_ICON[a.kind] || Wallet;
          const isCashbackBank = a.name.trim().toUpperCase() === "BANK CASHBACK";
          return (
            <Card key={a.name} data-testid={`account-${a.name}`} className={`p-4 hover:shadow-md transition-shadow ${isCashbackBank ? "border-dashed border-amber-300 bg-amber-50/40 dark:bg-amber-950/20" : ""}`}>
              <div className="flex items-center gap-2.5">
                <div className="grid place-items-center h-9 w-9 rounded-lg bg-secondary shrink-0">
                  <Icon className="h-4 w-4 text-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{a.name}</p>
                  <p className="text-[11px] text-muted-foreground">{a.kind}{a.custom ? " · Kustom" : ""}</p>
                </div>
                <div className="flex gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" title="Koreksi saldo"
                    onClick={() => { setEditFor(a); setNewBalance(String(a.balance)); }} data-testid={`account-edit-${a.name}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {a.custom && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" title="Hapus akun"
                      onClick={() => removeAccount(a)} data-testid={`account-delete-${a.name}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <p className={`font-heading text-xl font-extrabold mt-3 ${a.balance < 0 ? "text-rose-600" : ""}`} data-testid={`account-balance-${a.name}`}>{formatIDR(a.balance)}</p>
              {a.adjustment !== 0 && (
                <p className="text-[10px] text-muted-foreground mt-0.5">Sistem {formatIDR(a.computed)} · koreksi {a.adjustment > 0 ? "+" : ""}{formatIDR(a.adjustment)}</p>
              )}
              {isCashbackBank && (
                <div className="mt-2 rounded-lg bg-amber-100/70 dark:bg-amber-950/60 px-2.5 py-1.5" data-testid="account-cashback-auto">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300">Cashback belum dicairkan (otomatis)</p>
                  <p className="font-heading text-sm font-extrabold text-amber-600">{formatIDR(summary.cashback_pending || 0)}</p>
                </div>
              )}
            </Card>
          );
        })}
        {!accounts.some((a) => a.name.trim().toUpperCase() === "BANK CASHBACK") && (
        <Card data-testid="account-bank-cashback" className="p-4 border-dashed border-amber-300 bg-amber-50/40 dark:bg-amber-950/20 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2.5">
            <div className="grid place-items-center h-9 w-9 rounded-lg bg-amber-100 dark:bg-amber-950 shrink-0">
              <Gift className="h-4 w-4 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium truncate">Bank Cashback</p>
              <p className="text-[11px] text-muted-foreground">Otomatis · belum dicairkan</p>
            </div>
          </div>
          <p className="font-heading text-xl font-extrabold mt-3 text-amber-600" data-testid="account-balance-bank-cashback">{formatIDR(summary.cashback_pending || 0)}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Kewajiban cashback yang belum dicairkan — bukan saldo kas/bank. Berkurang otomatis saat cashback dicairkan.</p>
        </Card>
        )}
      </div>

      <Card className="p-4" data-testid="fund-distribution">
        <h3 className="font-heading text-sm font-semibold mb-3">Distribusi Dana</h3>
        <div className="space-y-2.5">
          {Object.entries(summary.accounts || {}).map(([name, val]) => (
            <div key={name} className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">{name}</span>
              <span className="text-xs font-semibold">{formatIDR(val)}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between rounded-lg bg-amber-50 dark:bg-amber-950/40 p-2.5">
            <div className="flex items-center gap-2">
              <ReceiptText className="h-4 w-4 text-amber-600" />
              <span className="text-xs font-medium">Tagihan belum lunas</span>
            </div>
            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300">
              {summary.outstanding_bill_count}
            </Badge>
          </div>
          <p className="mt-1.5 text-right font-heading font-bold text-base">{formatIDR(summary.outstanding_bills)}</p>
        </div>
      </Card>

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Koreksi Saldo Manual</DialogTitle>
            <DialogDescription className="text-xs">Selisih dicatat sebagai koreksi — transaksi baru tetap ikut menambah/mengurangi saldo.</DialogDescription>
          </DialogHeader>
          {editFor && (
            <div className="space-y-3 py-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Akun</span><span className="font-medium">{editFor.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Saldo terhitung sistem</span><span>{formatIDR(editFor.computed)}</span></div>
              {editFor.adjustment !== 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Koreksi saat ini</span><span>{editFor.adjustment > 0 ? "+" : ""}{formatIDR(editFor.adjustment)}</span></div>
              )}
              <div className="space-y-1 pt-1">
                <Label className="text-xs">Saldo Baru (Rp)</Label>
                <Input data-testid="acc-balance-input" type="number" className="h-9 text-xs" value={newBalance} onChange={(e) => setNewBalance(e.target.value)} placeholder="0" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditFor(null)}>Batal</Button>
            <Button onClick={saveBalance} disabled={busy} data-testid="acc-balance-save" variant="gold" size="sm" className="h-8 text-xs">Simpan Saldo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
